/**
 * Resolve a stable Session identifier from normalized or backend-shaped records.
 *
 * @param {object} session - Session record from frontend state or backend response.
 * @returns {string} Resolved session identifier, or an empty string when missing.
 */
export function getSessionId(session) {
  const record = recordValue(session);
  return firstTruthy(record.sessionId, record.session_id, record.id);
}

/**
 * Resolve a stable Project identifier for OBJ-02 list selection and rendering.
 *
 * @param {object} project - Project record from frontend state or backend response.
 * @returns {string} Resolved project identifier, or an empty string when missing.
 */
export function getProjectId(project) {
  const record = recordValue(project);
  return firstTruthy(record.project_id, record.id, record.key);
}

/**
 * Resolve the timestamp used for Project list ordering and display.
 *
 * @param {object} project - Project record from the normalized Project list.
 * @returns {string} Updated timestamp when present, otherwise created timestamp or an empty string.
 */
export function getProjectTimestamp(project) {
  const record = recordValue(project);
  const updatedAt = firstTruthy(record.updatedAt, record.updated_at);
  const createdAt = firstTruthy(record.createdAt, record.created_at);
  return firstTruthy(updatedAt, createdAt);
}

/**
 * Format an API timestamp for sidebar display.
 *
 * @param {string} value - Raw timestamp value from a Project or Session record.
 * @returns {string} Locale-formatted timestamp, or an empty string for invalid input.
 */
export function formatUpdatedAt(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString();
}

/**
 * Normalize session text for compact sidebar display.
 *
 * @param {string} value - Raw session title or preview text.
 * @returns {string} Plain text without common Markdown markers.
 */
export function normalizeSessionDisplayText(value) {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => String(line || '')
      .trim()
      .replace(/^#{1,6}\s*/, '')
      .replace(/^>\s*/, '')
      .replace(/^[-*+]\s+/, '')
      .replace(/^\d+[.)]\s+/, '')
      .replace(/^[*_`~]+|[*_`~]+$/g, '')
      .trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Resolve the user-facing session title for sidebar display.
 *
 * @param {object} session - Session record from frontend state or backend response.
 * @returns {string} Plain display title.
 */
export function resolveSessionDisplayTitle(session) {
  return normalizeSessionDisplayText(session?.title) || '未命名会话';
}

/**
 * Resolve the optional user-facing session preview for sidebar display.
 *
 * @param {object} session - Session record from frontend state or backend response.
 * @returns {string} Plain display preview, or empty when duplicated by title.
 */
export function resolveSessionDisplayPreview(session) {
  const title = normalizeSessionDisplayText(session?.title);
  const preview = normalizeSessionDisplayText(session?.preview || session?.description);
  return preview && preview !== title ? preview : '';
}
function recordValue(value) {
  return Object.assign({}, value);
}

function firstTruthy(...values) {
  return values.find(Boolean) ?? '';
}
